import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import type { BlogPostWithCategory } from "@shared/schema";
import { readingMinutes } from "./reading-time";
import { cleanExcerpt } from "./excerpt";
import { categoryVisual } from "@/lib/blog-visuals";

interface BlogPostCardProps {
  post: BlogPostWithCategory;
  showCategory?: boolean;
  /** Tighter card for the rows under a category's feature tile. */
  compact?: boolean;
  /** Heading level for the card's title. A card directly under the page's h1
   *  is an h2; one inside an already-headed sidebar section is an h3. */
  headingLevel?: 2 | 3;
}

/**
 * A guide as a card. Posts carry no photos, so the card leans on the
 * category's colour: a rule along the top, the category icon, and the
 * category name in that colour. Hover draws the rule the full width.
 */
export function BlogPostCard({ post, showCategory = true, compact = false, headingLevel = 2 }: BlogPostCardProps) {
  const Heading = (headingLevel === 3 ? "h3" : "h2") as "h2" | "h3";
  const minutes = readingMinutes(post);
  const visual = categoryVisual(post.category?.slug);
  const Icon = visual.icon;

  return (
    <article
      className={`group relative flex flex-col bg-shell transition-colors duration-300 hover:bg-sand ${compact ? "p-6 md:p-7" : "p-8 md:p-10"}`}
      data-testid={`card-blog-post-${post.id}`}
    >
      <span className="absolute left-0 top-0 h-[3px] w-10 transition-all duration-500 group-hover:w-full" style={{ backgroundColor: visual.accent }} aria-hidden="true" />

      <div className="mb-4 flex items-center gap-2.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full" style={{ backgroundColor: `${visual.accent}1A`, color: visual.accent }} aria-hidden="true">
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </span>
        {showCategory && post.category && (
          <span className="font-sans text-[10px] uppercase tracking-[0.28em]" style={{ color: visual.accent }}>
            {post.category.name}
          </span>
        )}
        {minutes !== null && (
          <span className="ml-auto font-sans text-[11px] uppercase tracking-[0.2em] text-driftwood">{minutes} min</span>
        )}
      </div>

      <Heading className={`mb-3 font-serif font-normal leading-tight tracking-tight text-forest ${compact ? "text-luxury-lg md:text-luxury-xl" : "text-luxury-xl md:text-luxury-2xl"}`}>
        <Link
          href={`/blog/${post.slug}`}
          className="transition-colors after:absolute after:inset-0 after:content-[''] group-hover:text-ink"
          data-testid={`link-post-${post.slug}`}
        >
          {post.title}
        </Link>
      </Heading>

      <p
        className={`flex-grow font-sans font-light leading-relaxed text-ink-soft ${compact ? "mb-5 text-[14px] line-clamp-3" : "mb-8 text-[15px]"}`}
        data-testid={`text-excerpt-${post.id}`}
      >
        {cleanExcerpt(post.title, post.excerpt, compact ? 120 : 180)}
      </p>

      <div className="mt-auto flex items-center justify-between">
        <span className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.24em] text-driftwood transition-colors group-hover:text-forest">
          Read guide
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </article>
  );
}
